import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

const FAB_SIZE = 56;
const EDGE = 8;
const PANEL_GAP = 12;
const PANEL_MAX_WIDTH = 380;
const PANEL_MAX_HEIGHT = 620;
const DRAG_THRESHOLD = 4;

type Point = { x: number; y: number };

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

const clampToViewport = ({ x, y }: Point): Point => ({
  x: clamp(x, EDGE, window.innerWidth - FAB_SIZE - EDGE),
  y: clamp(y, EDGE, window.innerHeight - FAB_SIZE - EDGE),
});

/**
 * Makes the floating button draggable anywhere inside the viewport.
 * Until the first drag the CSS default (bottom-right) applies; afterwards the
 * button is positioned by left/top and re-clamped whenever the window resizes.
 * The conversations panel follows the button.
 */
export const useDraggableFab = () => {
  const [position, setPosition] = useState<Point | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [, setViewportTick] = useState(0);
  const drag = useRef<{
    pointerId: number;
    origin: Point;
    start: Point;
    moved: boolean;
  } | null>(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    const onResize = () => {
      setPosition((current) => (current ? clampToViewport(current) : current));
      setViewportTick((tick) => tick + 1);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    drag.current = {
      pointerId: event.pointerId,
      origin: { x: event.clientX, y: event.clientY },
      start: { x: rect.left, y: rect.top },
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const dx = event.clientX - state.origin.x;
    const dy = event.clientY - state.origin.y;
    if (!state.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    state.moved = true;
    setIsDragging(true);
    setPosition(
      clampToViewport({ x: state.start.x + dx, y: state.start.y + dy })
    );
  };

  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    suppressClick.current = state.moved;
    drag.current = null;
    setIsDragging(false);
  };

  /** Wrap the real click handler so a drag release doesn't toggle the panel. */
  const guardClick = (handler: () => void) => () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    handler();
  };

  const fabStyle: CSSProperties | undefined = position
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : undefined;

  let panelStyle: CSSProperties | undefined;
  if (position) {
    const width = Math.min(PANEL_MAX_WIDTH, window.innerWidth - 32);
    const roomAbove = position.y - PANEL_GAP - EDGE;
    const roomBelow =
      window.innerHeight - (position.y + FAB_SIZE + PANEL_GAP) - EDGE;
    const openAbove = roomAbove >= roomBelow;
    const height = Math.min(
      PANEL_MAX_HEIGHT,
      openAbove ? roomAbove : roomBelow
    );
    panelStyle = {
      width,
      height,
      left: clamp(
        position.x + FAB_SIZE - width,
        EDGE,
        window.innerWidth - width - EDGE
      ),
      top: openAbove
        ? position.y - PANEL_GAP - height
        : position.y + FAB_SIZE + PANEL_GAP,
      right: 'auto',
      bottom: 'auto',
      transformOrigin: `${openAbove ? 'bottom' : 'top'} right`,
    };
  }

  return {
    isDragging,
    fabStyle,
    panelStyle,
    guardClick,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
};
